// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { FunctionsResponse, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { generateSubmissionIdentifier } from '@/utils/magic-link/helpers';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true, // This is crucial for OTP links to work correctly
    storage: typeof window !== 'undefined' ? localStorage : undefined, // Use localStorage for session persistence across tabs
    flowType: 'pkce', // Use PKCE flow for more secure authentication
  },
  global: {
    headers: {
      'X-Client-Info': 'supabase-js/2.x'
    }
  },
  db: {
    schema: 'public'
  }
});


const FUNCTION_RETRY_DELAYS_MS = [300, 600, 1200] as const;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getInvokeErrorStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') return undefined;

  const maybeStatus = (error as { status?: unknown }).status;
  if (typeof maybeStatus === 'number') return maybeStatus;

  const contextStatus = (error as { context?: { status?: unknown } }).context?.status;
  if (typeof contextStatus === 'number') return contextStatus;

  return undefined;
};

export async function invokeFunction<TData = unknown, TBody = unknown>(
  client: SupabaseClient,
  functionName: string,
  options?: { body?: TBody } & Record<string, unknown>,
): Promise<FunctionsResponse<TData>> {
  let firstRetryableError: unknown;

  for (let attempt = 0; attempt <= FUNCTION_RETRY_DELAYS_MS.length; attempt += 1) {
    const response = await client.functions.invoke<TData>(functionName, options);
    if (!response.error) return response;

    const status = getInvokeErrorStatus(response.error);
    if (status !== 503) {
      throw response.error;
    }

    firstRetryableError ??= response.error;
    if (attempt === FUNCTION_RETRY_DELAYS_MS.length) {
      throw firstRetryableError;
    }

    await sleep(FUNCTION_RETRY_DELAYS_MS[attempt]);
  }

  throw new Error('Unreachable invokeFunction retry state');
}

// Add a debug helper to validate that supabase is working
export const testSupabaseConnection = async () => {
  try {
    // Fix the count(*) issue by using .limit(1) instead of selecting count
    const { data, error } = await supabase.from('organisations').select('*').limit(1);

    if (error) {
      console.error('Supabase connection test failed:', error);
      return { success: false, error };
    }

    return { success: true, data };
  } catch (e) {
    console.error('Unexpected error in Supabase connection test:', e);
    return { success: false, error: e };
  }
};

// Add utility functions for magic links
export const sendMagicLink = async (email: string, redirectTo?: string, alternativeEmails?: string | string[]) => {
  try {
    // Generate a submission identifier
    const submissionIdentifier = generateSubmissionIdentifier();

    const finalRedirectTo = redirectTo || `${window.location.origin}/auth-callback?submission_identifier=${submissionIdentifier}`;

    // Ensure alternativeEmails is always an array or null
    let alternativeEmailsArray: string[] | null = null;

    if (alternativeEmails) {
      if (Array.isArray(alternativeEmails)) {
        alternativeEmailsArray = alternativeEmails.filter(email => email && typeof email === 'string');
      } else if (typeof alternativeEmails === 'string' && alternativeEmails.trim()) {
        alternativeEmailsArray = [alternativeEmails];
      }
    }

    // Determine which email to send the magic link to
    const destinationEmail = (alternativeEmailsArray && alternativeEmailsArray.length > 0)
      ? alternativeEmailsArray[0]
      : email;

    const { data, error } = await supabase.auth.signInWithOtp({
      email: destinationEmail,
      options: {
        emailRedirectTo: finalRedirectTo,
      }
    });

    if (error) throw error;

    // Store the email and alternative emails in the database via edge function
    if (email) {
      const { data, error: sessionError } = await invokeFunction(supabase, 'update-magic-link-session', {
        body: {
          action: 'upsert',
          sessionData: {
            email,
            alternative_email: alternativeEmailsArray,
            submission_identifier: submissionIdentifier
          }
        }
      });

      if (sessionError || !data?.success) {
        console.warn('Non-critical error storing magic link session:', sessionError?.message || data?.error);
      }
    }

    return {
      success: true,
      data,
      sentToAlternative: destinationEmail !== email
    };
  } catch (error) {
    console.error('Error sending magic link:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error sending magic link'
    };
  }
};

// Utility function to check if an email is in an alternative_email JSONB array
export const isEmailInAlternatives = (email: string, alternativeEmails: any): boolean => {
  if (!alternativeEmails) return false;

  // If it's already an array, check directly
  if (Array.isArray(alternativeEmails)) {
    return alternativeEmails.includes(email);
  }

  // If it's a string, try to parse it as JSON
  if (typeof alternativeEmails === 'string') {
    try {
      const parsed = JSON.parse(alternativeEmails);
      return Array.isArray(parsed) && parsed.includes(email);
    } catch (e) {
      return alternativeEmails === email;
    }
  }

  return false;
};
